import React, { Component } from "react";
import html2pdf from "html2pdf.js";
import axios from "axios"; // Import Axios
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Loading from "../component/Loading";
import { toast } from "react-toastify";
import Breadcrumb from "../component/Breadcrumb";
import Footer from "../component/Footer";
import { getDataBySerialNumber } from "../component/Apis"; // Import your API constant

const withRouter = (WrappedComponent) => (props) => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  return (
    <WrappedComponent
      {...props}
      navigate={navigate}
      params={params}
      location={location}
    />
  );
};

class CertificateDetails extends Component {
  state = {
    data: {},
    loading: true,
  };

  componentDidMount() {
    this.loadGoogleFonts();
    const { location, params, navigate } = this.props;

    // Check if we have the Serial Number in the URL (e.g., /certificate-details/12345)
    const urlSNumber = params.sNumber;

    // Scenario 1: User came from "Check Certificate" page (Data is in state)
    if (location.state && location.state.data) {
      this.setState({ data: location.state.data, loading: false });
      document.title = "Certificate";
    }
    // Scenario 2: User used a direct shared link (Data must be fetched via API)
    else if (urlSNumber) {
      this.fetchCertificateData(urlSNumber);
    }
    // Scenario 3: No State and No URL Param -> Redirect back
    else {
      navigate("/check-certificate");
    }
  }

  // New function to fetch data if visited via direct link
  fetchCertificateData = (sNumber) => {
    axios
      .get(`${getDataBySerialNumber}${atob(sNumber)}/`)
      .then((res) => {
        if (res.status === 200) {
          this.setState({ data: res.data, loading: false });
          document.title = "Certificate";
        } else {
          toast.error("Certificate not found");
          this.props.navigate("/check-certificate");
        }
      })
      .catch((error) => {
        console.error("API Error:", error);
        toast.error("Invalid Link or Server Error");
        this.props.navigate("/check-certificate");
      });
  };

  loadGoogleFonts = () => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;800&display=swap";
    document.head.appendChild(link);
  };

  formatDate = (dateString) => {
    if (!dateString) return "May 2025";
    const date = new Date(dateString);
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return `${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  preloadImages = (element) => {
    const imgs = Array.from(element.querySelectorAll("img"));
    if (!imgs.length) return Promise.resolve();
    return Promise.all(
      imgs.map(
        (img) =>
          new Promise((resolve) => {
            if (img.complete && img.naturalWidth !== 0) return resolve();
            img.crossOrigin = "anonymous";
            img.onload = () => resolve();
            img.onerror = () => {
              console.warn("Image failed to load for PDF:", img.src);
              resolve();
            };
          }),
      ),
    );
  };

  downloadAsPDF = async () => {
    const certificateElement = document.querySelector(".certificate");
    const certificateBG = document.querySelector(".bgCertificateImage");

    if (!certificateElement || !certificateBG) {
      toast.error("Certificate element not found");
      return;
    }

    toast.info("Preparing certificate for PDF...", { autoClose: 2000 });

    certificateBG.style.display = "flex";
    certificateElement.style.display = "block";

    try {
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await this.preloadImages(certificateElement);

      const opt = {
        margin: 0,
        filename: `Certificate_${(this.state.data.name || "Certificate").replace(/\s+/g, "_")}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          logging: false,
        },
        jsPDF: {
          unit: "mm",
          format: [300, 450],
          orientation: "portrait",
        },
      };

      await html2pdf().set(opt).from(certificateElement).save();
      toast.success("Certificate downloaded successfully!");
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.error("Failed to generate PDF. See console for details.");
    } finally {
      certificateBG.style.display = "none";
      certificateElement.style.display = "none";
    }
  };

  downloadDetailsAsPDF = async () => {
    const detailsElement = document.querySelector(".certificate_card");
    if (!detailsElement) {
      toast.error("Certificate details element not found");
      return;
    }
    toast.info("Generating details PDF...", { autoClose: 2000 });
    try {
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await this.preloadImages(detailsElement);
      const opt = {
        margin: [15, 15, 15, 15],
        filename: `Certificate_Details_${(this.state.data.name || "Details").replace(/\s+/g, "_")}.pdf`,
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: null },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      };
      await html2pdf().set(opt).from(detailsElement).save();
      toast.success("Certificate details downloaded successfully!");
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.error("Failed to generate details PDF. See console for details.");
    }
  };

  render() {
    const {
      sNumber,
      traningName,
      name,
      date,
      gpa,
      durationByHour,
      location,
      centerName,
    } = this.state.data;
    const { loading } = this.state;

    return (
      <>
        {/* UPDATED: Styles for the report card background */}
        <style>{`
          .certificate_detials { background-color: #fff; padding: 50px 0; position: relative; }
          .certificate_card { 
            width: 80%; margin: auto; 
            border-radius: 10px; box-shadow: 0 5px 15px rgba(0, 0, 0, 0.4);
            border: 1px solid black;
            position: relative;
            overflow: hidden;
          }
          .report-bg-img, .report-overlay { position: absolute; top: 0; left: 0; width: 100%; height: 100%; z-index: 1; }
          .report-bg-img { object-fit: cover; }
          .report-overlay { background-color: rgba(255, 255, 255, 0.2); z-index: 2; }
          .report-content { position: relative; z-index: 3; padding: 30px; }
          .report-header { display: flex; justify-content: space-between; align-items: center; padding-bottom: 20px; margin-bottom: 20px; border-bottom: 1px solid #eee; }
          .report-header img { max-height: 60px; width: auto; }
          .certificate_card li { display: flex; padding: 15px 0; border-bottom: 1px solid #eee; align-items: center; justify-content: space-between; background: transparent; }
          .certificate_card li:last-child { border-bottom: none; }
          .certificate_card li .title { font-weight: bold; font-size: 1.2rem; }
          .certificate_card li .value { font-size: 1.1rem; }
          .report-footer { display: flex; justify-content: space-between; align-items: center; padding-top: 20px; margin-top: 20px; border-top: 1px solid #eee; }
          .report-footer img { max-height: 50px; width: auto; }
          .report-footer .contact-info { font-size: 0.8rem; text-align: center; color: #666; }
          .report-footer .contact-info p { margin: 2px 0; }
        `}</style>

        <Breadcrumb title="Certificate Details" image="cerdetials.jpg" />
        <div className="certificate_detials">
          <div className="container">
            <div className="certificate_card">
              <img
                src={`${process.env.PUBLIC_URL}/images/certificate/bg.png`}
                className="report-bg-img"
                alt=""
                crossOrigin="anonymous"
              />
              <div className="report-overlay"></div>
              <div className="report-content">
                <div className="card_body">
                  <div className="report-header">
                    <img
                      crossOrigin="anonymous"
                      src={`${process.env.PUBLIC_URL}/images/certificate/America flag.png`}
                      alt="American Flag"
                    />
                    <img
                      crossOrigin="anonymous"
                      src={`${process.env.PUBLIC_URL}/images/certificate/logo.png`}
                      alt="ABPS Logo"
                    />
                    <img
                      crossOrigin="anonymous"
                      src={`${process.env.PUBLIC_URL}/images/certificate/QR.png`}
                      alt="QR Code"
                    />
                  </div>
                  <ul className="list-unstyled">
                    <li>
                      <span className="title">Serial Number</span>{" "}
                      <span className="value">{sNumber}</span>
                    </li>
                    <li>
                      <span className="title">Center Name</span>{" "}
                      <span className="value">{centerName}</span>
                    </li>
                    <li>
                      <span className="title">Training Name</span>{" "}
                      <span className="value">{traningName}</span>
                    </li>
                    <li>
                      <span className="title">Name</span>{" "}
                      <span className="value">{name}</span>
                    </li>
                    <li>
                      <span className="title">Date</span>{" "}
                      <span className="value">{date?.split("T")[0]}</span>
                    </li>
                    <li>
                      <span className="title">Grade</span>{" "}
                      <span className="value">{gpa}</span>
                    </li>
                    <li>
                      <span className="title">Duration</span>{" "}
                      <span className="value">{durationByHour} Hour</span>
                    </li>
                    <li>
                      <span className="title">Location</span>{" "}
                      <span className="value">{location}</span>
                    </li>
                  </ul>
                  <div className="report-footer">
                    <img
                      crossOrigin="anonymous"
                      src={`${process.env.PUBLIC_URL}/images/certificate/gold.png`}
                      alt="Gold Seal"
                    />
                    <div className="contact-info">
                      <p>30 N GOULD ST, Sheridan, WY, 82801, USA</p>
                      <p>https://Americanboard-us.com</p>
                    </div>
                    <img
                      crossOrigin="anonymous"
                      src={`${process.env.PUBLIC_URL}/images/certificate/signature.png`}
                      alt="Signature"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bgCertificateImage" style={{ display: "none" }}>
          <style jsx>{`
            .bgCertificateImage {
              position: fixed;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              background: rgba(0, 0, 0, 0.5);
              z-index: 9999;
              display: flex;
              align-items: center;
              justify-content: center;
              overflow: auto;
            }
            .certificate {
              position: relative;
              background: white;
              width: 300mm;
              height: 450mm;
              overflow: hidden;
              font-family:
                "Playfair Display", "Times New Roman", Arial, sans-serif;

              box-sizing: border-box;
            }
            .certificate-bg-img {
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              object-fit: cover;
              z-index: 1;
            }
            .certificate-content {
              position: relative;
              z-index: 3;
              width: 95%;
              height: 100%;
              padding: 12mm;
              box-sizing: border-box;
              border: 1px solid black; /* New border */
              display: flex;
              flex-direction: column;
              justify-content: space-between;
            }
            .watermark {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              z-index: 2;
              opacity: 0.05;
            }
            .watermark-img {
              width: 60mm;
            }
            .certificate-header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              margin-bottom: 10mm;
              padding: 0 5mm;
            }
            .american-flag-img {
              width: 40mm;
              height: auto;
            }
            .logo-img {
              width: 40mm;
            }
            .qr-code-img {
              width: 40mm;
              height: 40mm;
              object-fit: contain;
            }
            .certificate-title {
              text-align: center;
              margin-bottom: 8mm;
            }
            .certificate-title h1 {
              font-size: 32pt;
            }
            .certificate-title h2 {
              font-size: 26pt;
            }
            .certificate-title h3 {
              margin-top: 30px;
              font-weight: 700;
            }
            .certificate-body {
              flex: 1;
              display: flex;
              flex-direction: column;
              justify-content: flex-start;
              text-align: center;
              padding: 0 12mm;
            }
            .recognition-text p,
            .recipient-info p,
            .certification-text p,
            .privileges-text p {
              font-size: 20pt;
              line-height: 1.4;
              margin: 4px 0;
            }
            .recipient-name {
              font-size: 22pt;
              font-weight: 700;
              color: #991c21;
              margin: 30px 0 30px;
            }
            .diploma-title {
              font-weight: bold;
              font-size: 19pt;
            }
            .issued {
              color: #991c21 !important; /* New color */
              font-weight: bold;
              margin-bottom: 40px;
            }
            .certificate-footer {
              display: flex;
              justify-content: space-between;
              align-items: flex-end;
              margin-top: 10mm;
              padding: 0 12mm;
            }
          `}</style>

          <div
            className="certificate"
            id="certificate-root"
            style={{ display: "none" }}
          >
            <img
              src={`${process.env.PUBLIC_URL}/images/certificate/bg.png`}
              className="certificate-bg-img"
              alt="Certificate Background"
              crossOrigin="anonymous"
            />
            <div className="watermark">
              <img
                crossOrigin="anonymous"
                src={`${process.env.PUBLIC_URL}/images/watermark-logo.svg`}
                alt="Watermark"
                className="watermark-img"
              />
            </div>
            <div className="certificate-content">
              <div className="certificate-header">
                <div className="flag-section">
                  <img
                    crossOrigin="anonymous"
                    src={`${process.env.PUBLIC_URL}/images/certificate/America flag.png`}
                    alt="American Flag"
                    className="american-flag-img"
                  />
                </div>
                <div className="logo-section">
                  <img
                    crossOrigin="anonymous"
                    src={`${process.env.PUBLIC_URL}/images/certificate/logo.png`}
                    alt="ABPS Logo"
                    className="logo-img"
                  />
                </div>
                <div className="qr-section">
                  <img
                    crossOrigin="anonymous"
                    src={`${process.env.PUBLIC_URL}/images/certificate/QR.png`}
                    alt="QR Code"
                    className="qr-code-img"
                  />
                </div>
              </div>
              <div className="certificate-body">
                <div className="certificate-title">
                  <h1>CERTIFICATE</h1>
                  <h2>American Board of Professional Studies</h2>
                  <h3>CERTIFICATE OF DIPLOMA</h3>
                </div>
                <div className="recognition-text">
                  <p>
                    IN RECOGNITION OF THE REQUISITE STUDIES & ON NOMINATION BY
                    THE BOARD
                  </p>
                  <p>THIS IS TO CERTIFY THAT</p>
                </div>
                <div className="recipient-info">
                  <div className="recipient-name">
                    {name || "RECIPIENT NAME"}
                  </div>
                  <p>Having Participated in the training diploma</p>
                  <div className="diploma-title">
                    {traningName?.replace("(", "")?.replace(")", "") ||
                      "TRAINING PROGRAM"}
                  </div>
                  <p>
                    And he/she has successfully met the professional standards
                    established for recognition as a professional trainee with
                    Active status in good standing of the American Board of
                    Professional Studies and is hereby granted this certificate
                    of Proficiency
                  </p>
                </div>
                <div className="certification-text">
                  <p className="issued">Issued on: {this.formatDate(date)}</p>
                  <p>With all honors, rights and privileges granted</p>
                  <p>
                    under the rules and regulations of the American Board of
                    Professional Studies
                  </p>
                </div>
                <div className="privileges-text">
                  <p>CERTIFICATE SERIAL NO: {sNumber || "0"}</p>
                  <p>Duration: {durationByHour || "60"} Hours</p>
                  <p>Grade: {gpa || "Excellent"}</p>
                </div>
              </div>
              <div className="certificate-footer">
                <div className="footer-left">
                  <img
                    crossOrigin="anonymous"
                    src={`${process.env.PUBLIC_URL}/images/certificate/gold.png`}
                    alt="brand"
                  />
                </div>
                <div className="footer-center contact-info">
                  <p>30 N GOULD ST,</p>
                  <p>Sheridan, WY, 82801, USA</p>
                  <p>To Verify your Certificate,</p>
                  <p>please Visit:</p>
                  <p>https://Americanboard-us.com</p>
                </div>
                <div className="footer-right">
                  <img
                    crossOrigin="anonymous"
                    src={`${process.env.PUBLIC_URL}/images/certificate/signature.png`}
                    alt="Signature"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            textAlign: "center",
            padding: "20px 0 40px",
            background: "#fff",
          }}
        >
          <button
            onClick={this.downloadDetailsAsPDF}
            className="btn btn-primary"
            style={{ marginRight: "10px" }}
          >
            Download Report
          </button>
          <button onClick={this.downloadAsPDF} className="btn btn-danger">
            Download Certificate
          </button>
        </div>

        {loading ? <Loading /> : null}
        <Footer />
      </>
    );
  }
}

export default withRouter(CertificateDetails);
